import type { ESRIMap } from '@rsos/maps/providers/esri/types';
import type { MapViewState } from './mapViewState/mapViewState.types';

/**
 * Shape of `window.__RSOS`, the namespace `exportToGlobalNamespace` writes to.
 * Importing from this module activates the `Window` augmentation below; an
 * ambient `.d.ts` would not cross workspace-package boundaries.
 */
export interface ESRIMapProps {
  mapProvider: 'esri';
  mapInstance: ESRIMap;
  /** Supplied by the map's creator, so shared code never imports @arcgis/core. */
  getViewState?: (esriBasemap?: string) => MapViewState | null;
}

export interface GoogleMapProps {
  mapProvider: 'google';
  mapInstance: google.maps.Map;
}

interface MessageCallbacks {
  [key: string]: (message: Record<string, unknown>) => void;
}

export interface RSOSGlobals {
  map?: ESRIMapProps | GoogleMapProps;
  hermes?: {
    instance?: {
      addOnMessageCallback: (callbacks: MessageCallbacks) => void;
      removeOnMessageCallback: (messageName: string) => void;
    };
  };
  streamWS?: {
    instance?: {
      addOnMessageCallback: (callbacks: MessageCallbacks) => void;
      removeOnMessageCallback: (messageName: string) => void;
      addOnReconnectCallback: (callbacks: {
        [key: string]: () => void;
      }) => void;
      removeOnReconnectCallback: (callbackName: string) => void;
    };
    /** Set by StreamWS.js via exportToGlobalNamespace. */
    handle?: (...args: unknown[]) => void;
  };
  talkjsSession?: {
    getOrCreateConversation: (conversationId: string) => Promise<{
      sendMessage: (message: string) => unknown;
    }>;
  };
  flags?: {
    override: (flagKey: string, flagValue: boolean) => void;
    list: () => Record<string, boolean>;
  };
  mapViewState?: MapViewState;
}

declare global {
  interface Window {
    __RSOS?: RSOSGlobals;
  }
}

/** `window.__RSOS.map`, typed as the provider union so `mapProvider` narrows. */
export const getRSOSMap = (): RSOSGlobals['map'] => window.__RSOS?.map;
